import { Link } from "react-router-dom";
import { CheckCircle2, Circle, ArrowUpRight, Clock } from "lucide-react";
import { TOOLS } from "@/data/tools";
import { Panel, fmtDate } from "@/components/rc/shared";

const QS = [
  ["qs-health-v3", "Health Check"], ["qs-provision-v2", "Provision Review"], ["qs-ehcp-v3", "EHCP Before, During and After"],
  ["qs-parent-v3", "Parent Partnership"], ["qs-plan-v2", "Action Planner"], ["qs-notes", "Improvement Notes"],
];

const latest = (saves, match) => Object.entries(saves).filter(([k]) => match(k)).map(([, v]) => v)
  .sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""))[0];

export const toolkitStatus = (saves = {}) => [
  { id: "t1", title: "Toolkit 1: Quick Start Pack", items: QS.map(([key, title]) => ({ id: key, title, href: "/quick-start#qs-pack", saved: latest(saves, (k) => k === key) })) },
  {
    id: "t2", title: "Toolkit 2: SENDCO Resource Pack",
    items: TOOLS.filter((t) => t.id !== "resource-directory").map((t) => ({
      id: t.id, title: t.title, href: `/#${t.id}`,
      saved: latest(saves, (k) => k.startsWith(t.id) || (t.id === "parent-partnership" && k === "parent-letters")),
    })),
  },
];

const Item = ({ it }) => (
  <li>
    <Link to={it.href} className="group flex items-center justify-between gap-3 rounded-2xl border-2 border-line bg-paper p-4 transition-colors hover:border-brand" data-testid={`ws-tool-${it.id}`}>
      <span className="flex items-start gap-3">
        {it.saved ? <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-brand" aria-hidden /> : <Circle size={20} className="mt-0.5 shrink-0 text-ink-soft" aria-hidden />}
        <span>
          <span className="block font-bold">{it.title}</span>
          <span className="block text-sm text-ink-soft">{it.saved ? `Saved ${fmtDate(it.saved.updated_at)} by ${it.saved.updated_by}` : "Not started"}</span>
        </span>
      </span>
      <ArrowUpRight size={18} className="shrink-0 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden />
    </Link>
  </li>
);

export const ToolkitsPanel = ({ saves, readOnly }) => (
  <Panel id="ws-toolkits" eyebrow={readOnly ? "View only" : "Auto-saves to your workspace"} title="Your toolkits">
    <p className="mb-6 max-w-2xl text-ink-soft">
      {readOnly ? "You can open and read every toolkit. As a governor, your changes stay in this browser and are not saved to the workspace." : "Open any toolkit and fill it in as normal. While you're signed in, every answer saves to your school's workspace automatically, so you and your colleagues can return to it on any device."}
    </p>
    <div className="grid gap-8 lg:grid-cols-2">
      {toolkitStatus(saves).map((g) => (
        <div key={g.id}>
          <h3 className="mb-3 font-display text-2xl font-medium">{g.title}</h3>
          <ul className="space-y-2">{g.items.map((it) => <Item key={it.id} it={it} />)}</ul>
        </div>
      ))}
    </div>
    <div className="mt-8 grid gap-3 sm:grid-cols-2">
      {["Toolkit 3", "Toolkit 4"].map((t) => (
        <p key={t} className="flex items-center gap-3 rounded-2xl border-2 border-dashed border-line p-4 font-bold text-ink-soft" data-testid={`ws-${t.replace(" ", "-").toLowerCase()}-soon`}>
          <Clock size={18} aria-hidden /> {t}: coming soon
        </p>
      ))}
    </div>
  </Panel>
);
